import { normalizeActionButton } from './laikaActionClick';
import type { ActionButton } from './types';

export type LaikaSseEvent = {
  type: string;
  message?: string;
  tool?: string;
  data?: {
    response?: string;
    conversation_id?: string | null;
    actions?: ActionButton[];
    sources?: string[];
    detail?: string;
  };
};

export type LaikaStreamResult = {
  response: string;
  conversationId: string | null;
  actions: ActionButton[];
  sources: string[];
  lastStatus: string | null;
};

const STALL_PREFIX =
  /^(?:Je vais (?:récupérer|vérifier|te montrer|t'afficher|chercher|regarder)[^\n.]*\.\s*|Un instant[^\n.]*\.\s*|Laisse[- ]?moi (?:vérifier|chercher|regarder)[^\n.]*\.\s*|Attends?,? je (?:vérifie|regarde|cherche)[^\n.]*\.\s*|Je (?:vérifie|récupère|cherche)[^\n.]*\.\s*)/i;

const STALL_INCOMPLETE =
  /^(?:Je vais |Un instant|Laisse[- ]?moi |Attends?,? je |Je (?:vérifie|récupère|cherche) )/i;

/** SSE repeats the FE workingNow line in French — skip so i18n stays. */
export function isRedundantOccupyStatus(message: string): boolean {
  const trimmed = (message || '').trim();
  return (
    /^je m['’]occupe de ta demande/i.test(trimmed) ||
    /^c['’]est parti[.…]*$/i.test(trimmed)
  );
}

/** Hide « Je vais vérifier… » while tokens stream; keep the real answer. */
export function visibleLaikaDraft(text: string): string {
  let out = text || '';
  out = out.replace(STALL_PREFIX, '');
  if (STALL_INCOMPLETE.test(out) && !/[.!?]\s|\n/.test(out.slice(0, 120))) {
    return '';
  }
  return out;
}

/**
 * Parse a raw SSE chunk buffer into events (tolerant to partial frames).
 * Returns consumed events and remaining buffer.
 */
export function parseLaikaSseBuffer(buffer: string): {
  events: LaikaSseEvent[];
  rest: string;
} {
  const parts = buffer.split('\n\n');
  const rest = parts.pop() ?? '';
  const events: LaikaSseEvent[] = [];
  for (const part of parts) {
    const dataLine = part
      .split('\n')
      .map((l) => l.trimEnd())
      .find((l) => l.startsWith('data: '));
    if (!dataLine) continue;
    try {
      events.push(JSON.parse(dataLine.slice('data: '.length)) as LaikaSseEvent);
    } catch {
      // ignore malformed frame
    }
  }
  return { events, rest };
}

export async function consumeLaikaSseResponse(
  response: Response,
  onStatus?: (message: string) => void,
  onPartial?: (text: string) => void,
): Promise<LaikaStreamResult> {
  if (!response.body) {
    throw new Error('Laika stream: empty body');
  }
  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  const acc: {
    lastStatus: string | null;
    partial: string;
    finalEvent: LaikaSseEvent | null;
    errorMessage: string | null;
  } = {
    lastStatus: null,
    partial: '',
    finalEvent: null,
    errorMessage: null,
  };

  const applyEvent = (ev: LaikaSseEvent) => {
    if ((ev.type === 'status' || ev.type === 'tool_start') && ev.message) {
      if (isRedundantOccupyStatus(ev.message)) {
        return;
      }
      acc.lastStatus = ev.message;
      onStatus?.(ev.message);
    }
    if (ev.type === 'token' && ev.message) {
      acc.partial += ev.message;
      const visible = visibleLaikaDraft(acc.partial);
      if (visible) onPartial?.(visible);
    }
    if (ev.type === 'final') {
      acc.finalEvent = ev;
    }
    if (ev.type === 'error') {
      acc.errorMessage = ev.message || 'stream error';
    }
  };

  let streamDone = false;
  while (!streamDone) {
    const { done, value } = await reader.read();
    streamDone = done;
    if (value) {
      buffer += decoder.decode(value, { stream: !done });
    }
    const parsed = parseLaikaSseBuffer(buffer);
    buffer = parsed.rest;
    for (const ev of parsed.events) {
      applyEvent(ev);
    }
    if (done && buffer.trim()) {
      const flushed = parseLaikaSseBuffer(
        buffer.endsWith('\n\n') ? buffer : `${buffer}\n\n`,
      );
      for (const ev of flushed.events) {
        applyEvent(ev);
      }
    }
  }

  const finalData = acc.finalEvent?.data;
  if (acc.errorMessage && !finalData) {
    throw new Error(acc.errorMessage);
  }
  if (!finalData?.response) {
    throw new Error('Laika stream: missing final response');
  }

  return {
    response: finalData.response,
    conversationId: finalData.conversation_id ?? null,
    actions: (finalData.actions || []).map((a) => normalizeActionButton(a)),
    sources: Array.isArray(finalData.sources)
      ? finalData.sources.map((s) => String(s)).filter(Boolean)
      : [],
    lastStatus: acc.lastStatus,
  };
}
